import React, { useEffect, useMemo, useState } from "react";
import { calculateTapDrill, THREAD_SIZE_OPTIONS } from "../../utils/calculations";

const THREAD_TYPES = [
  ["60-degree", "Unified / ISO 60 degree thread"],
  ["UN", "UN / UNS special"],
  ["UNEF", "UNEF extra fine"],
  ["NPT", "NPT / tapered pipe"],
  ["BSP", "BSP / pipe thread"],
  ["ACME", "ACME / trapezoidal"],
  ["custom", "Custom thread form"],
];

const TapDrillCalculator = ({ onSave }) => {
  const [params, setParams] = useState({
    entryMode: "preset",
    threadSize: "1/4-20",
    threadPercent: 75,
    customUnit: "inch",
    pitchMode: "tpi",
    customMajorDiameter: "0.250",
    customPitch: "20",
    threadSeries: "60-degree",
    threadName: "",
  });
  const [search, setSearch] = useState("");
  const [results, setResults] = useState(null);

  useEffect(() => {
    setResults(calculateTapDrill(params));
  }, [params]);

  const filteredThreads = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return THREAD_SIZE_OPTIONS;
    return THREAD_SIZE_OPTIONS.filter((thread) =>
      [thread.label, thread.value, thread.unit, thread.threadSeries, thread.standard]
        .join(" ")
        .toLowerCase()
        .includes(query)
    );
  }, [search]);

  const handleInputChange = (field, value) => {
    setParams((previous) => ({ ...previous, [field]: value }));
  };

  const resetForm = () => {
    setParams({
      entryMode: "preset",
      threadSize: "1/4-20",
      threadPercent: 75,
      customUnit: "inch",
      pitchMode: "tpi",
      customMajorDiameter: "0.250",
      customPitch: "20",
      threadSeries: "60-degree",
      threadName: "",
    });
    setSearch("");
  };

  const copyToClipboard = () => {
    if (!results || results.errors) return;
    navigator.clipboard.writeText(
      [
        "ManuCalc - Tap Drill",
        `Thread: ${results.threadLabel}`,
        `Thread type: ${results.threadSeries}`,
        `Recommended tap drill: ${results.tapDrillSize && results.tapDrillMetric ? `${results.tapDrillSize} in / ${results.tapDrillMetric} mm` : results.display}`,
        `Nearest drill: ${results.display}`,
        `Conservative drill at/below target: ${results.conservativeDisplay}`,
        `Thread engagement: ${results.threadFamily === "pipe" ? results.threadPercent : `${results.threadPercent}%`}`,
      ].join("\n")
    );
  };

  const handleSave = () => {
    if (!results || results.errors || !onSave) return;

    onSave({
      summary: `${results.threadLabel} | ${results.display} | ${results.threadPercent}% thread`,
      input: params,
      results,
      warnings: results.warnings || [],
      confidence: results.confidence || "N/A",
    });
  };

  const hasErrors = results?.errors && Object.keys(results.errors).length > 0;
  const isCustom = params.entryMode === "custom";
  const isMetricCustom = params.customUnit === "metric";

  return (
    <div className="calculator-container">
      <div className="calculator-header" style={{ marginBottom: "24px" }}>
        <h1 style={{ color: "var(--text-primary)", marginBottom: "8px" }}>
          Tap Drill Calculator
        </h1>
        <p style={{ color: "var(--text-muted)", margin: 0 }}>
          Find tap drill sizes from standard thread presets or any custom major
          diameter and pitch/TPI. The math uses the common major-diameter minus
          pitch tap drill approximation and clearly labels custom thread forms
          as estimates.
        </p>
      </div>

      <div className="calculator-content" style={layoutStyle}>
        <div className="calculator-form">
          <div className="form-section" style={{ marginBottom: "24px" }}>
            <div className="form-group">
              <label>Input Mode</label>
              <select
                value={params.entryMode}
                onChange={(event) => handleInputChange("entryMode", event.target.value)}
              >
                <option value="preset">Standard thread list</option>
                <option value="custom">Any custom thread</option>
              </select>
            </div>

            {!isCustom ? (
              <>
                <div className="form-group">
                  <label>Search Threads</label>
                  <input
                    type="search"
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    placeholder="Search #10-32, 1/4-20, M8, metric..."
                  />
                </div>

                <div className="form-group">
                  <label>Thread Size</label>
                  <select
                    value={params.threadSize}
                    onChange={(event) => handleInputChange("threadSize", event.target.value)}
                  >
                    {filteredThreads.map((thread) => (
                      <option key={thread.value} value={thread.value}>
                        {thread.label}
                      </option>
                    ))}
                  </select>
                </div>
              </>
            ) : (
              <>
                <div className="form-group">
                  <label>Thread Name / Callout</label>
                  <input
                    value={params.threadName}
                    onChange={(event) => handleInputChange("threadName", event.target.value)}
                    placeholder="Example: 7/8-18 UNS, M22 x 1.5, 1/8 NPT"
                  />
                </div>

                <div className="form-group">
                  <label>Thread Type</label>
                  <select
                    value={params.threadSeries}
                    onChange={(event) => handleInputChange("threadSeries", event.target.value)}
                  >
                    {THREAD_TYPES.map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Unit</label>
                  <select
                    value={params.customUnit}
                    onChange={(event) => handleInputChange("customUnit", event.target.value)}
                  >
                    <option value="inch">Inch</option>
                    <option value="metric">Metric</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Major Diameter ({isMetricCustom ? "mm" : "in"})</label>
                  <input
                    value={params.customMajorDiameter}
                    onChange={(event) => handleInputChange("customMajorDiameter", event.target.value)}
                    placeholder={isMetricCustom ? "Example: 22" : "Example: 0.875 or 7/8"}
                  />
                </div>

                {!isMetricCustom && (
                  <div className="form-group">
                    <label>Pitch Input</label>
                    <select
                      value={params.pitchMode}
                      onChange={(event) => handleInputChange("pitchMode", event.target.value)}
                    >
                      <option value="tpi">Threads per inch</option>
                      <option value="pitch">Pitch in inches</option>
                    </select>
                  </div>
                )}

                <div className="form-group">
                  <label>{isMetricCustom ? "Pitch (mm)" : params.pitchMode === "tpi" ? "Threads per Inch" : "Pitch (in)"}</label>
                  <input
                    value={params.customPitch}
                    onChange={(event) => handleInputChange("customPitch", event.target.value)}
                    placeholder={isMetricCustom ? "Example: 1.5" : params.pitchMode === "tpi" ? "Example: 18" : "Example: 0.0556"}
                  />
                </div>
              </>
            )}

            <div className="form-group">
              <label>Thread Engagement (%)</label>
              <input
                type="number"
                min="50"
                max="85"
                step="1"
                value={params.threadPercent}
                onChange={(event) => handleInputChange("threadPercent", event.target.value)}
              />
            </div>
          </div>

          <button onClick={resetForm} className="btn-secondary" type="button">
            Reset
          </button>
        </div>

        <div className="calculator-results">
          {results && !hasErrors ? (
            <>
              <h3>Results</h3>
              <Result label="Thread" value={results.threadLabel} />
              <Result label="Thread Type" value={results.threadSeries} />
              {results.standard && <Result label="Standard" value={results.standard} />}
              {results.taper && <Result label="Pipe Thread Form" value={results.taper} />}
              <Result
                label="Calculated Tap Drill"
                value={results.tapDrillSize && results.tapDrillMetric ? `${results.tapDrillSize} in / ${results.tapDrillMetric} mm` : results.display}
              />
              <Result label="Nearest Standard Drill" value={results.display} />
              <Result label="Drill At/Below Target" value={results.conservativeDisplay} />
              <Result label="Major Diameter" value={results.unit === "metric" ? `${results.majorDiameter} mm` : `${results.majorDiameter} in`} />
              <Result label="Pitch" value={results.unit === "metric" ? `${results.pitch} mm` : `${results.threadsPerInch} TPI / ${results.pitchInch} in pitch`} />
              {results.handTightEngagement && <Result label="Hand-Tight Engagement" value={`${results.handTightEngagement} in`} />}
              {results.effectiveThreadLength && <Result label="Effective Thread Length" value={`${results.effectiveThreadLength} in`} />}
              <Result label="Thread Engagement" value={results.threadFamily === "pipe" ? results.threadPercent : `${results.threadPercent}%`} />

              <div style={noteStyle}>
                Shop note: standard cutting tap drill math is a practical
                starting point. Verify thread class, fit, plating allowance,
                thread-forming taps, pipe-thread standards, and toolmaker data
                before production.
              </div>

              {results.warnings?.length > 0 && (
                <ul style={{ color: "#f59e0b", paddingLeft: "20px" }}>
                  {results.warnings.map((warning) => (
                    <li key={warning}>{warning}</li>
                  ))}
                </ul>
              )}

              <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
                <button onClick={handleSave} className="btn-primary" type="button">
                  Save Calculation
                </button>
                <button onClick={copyToClipboard} className="btn-secondary" type="button">
                  Copy
                </button>
              </div>
            </>
          ) : (
            <div style={{ color: "var(--text-muted)" }}>
              Enter a valid preset or custom thread.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const Result = ({ label, value }) => (
  <div style={{ marginBottom: "12px" }}>
    <strong style={{ color: "var(--text-primary)" }}>{label}:</strong>{" "}
    <span>{value}</span>
  </div>
);

const layoutStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
  gap: "24px",
};

const noteStyle = {
  margin: "16px 0",
  padding: "12px",
  borderRadius: "12px",
  border: "1px solid var(--border-soft)",
  background: "rgba(255, 255, 255, 0.055)",
  color: "var(--text-muted)",
  lineHeight: 1.5,
};

export default TapDrillCalculator;
